import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { 
  ArrowLeftIcon, 
  ArrowPathIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  PlayIcon,
  QueueListIcon,
} from '@heroicons/react/24/outline';
import Card, { CardHeader, CardBody } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Spinner from '@/components/ui/Spinner';
import { useApi } from '@/hooks/useApi';
import type { Distributor, ConnectorJobsListResponse } from '@/types/api';

function getDistributorDisplayName(d: { distributor_name?: string; name?: string; code?: string }): string {
  return d.distributor_name || d.name || d.code || '—';
}

// API response wraps distributor in a 'distributor' property
interface DistributorDetailResponse {
  id: string;
  distributor: Distributor;
}

// Platform icon mapping
const PLATFORM_ICONS: Record<string, string> = {
  'apple': '🍎',
  'google': '📚',
  'spotify': '🎧',
  'sftp': '📁',
  'default': '🌐',
};

function getPlatformIcon(code: string): string {
  return PLATFORM_ICONS[code.toLowerCase()] || PLATFORM_ICONS.default;
}

// Status badge helpers
function getStatusBadge(active: boolean | undefined) {
  if (active === true) {
    return <Badge variant="success">Active</Badge>;
  }
  return <Badge variant="slate">Inactive</Badge>;
}

function getModeBadge(mode: string) {
  switch (mode) {
    case 'push':
      return <Badge variant="primary">Push</Badge>;
    case 'pull':
      return <Badge variant="warning">Pull</Badge>;
    case 'hybrid':
      return <Badge variant="secondary">Hybrid</Badge>;
    default:
      return <Badge variant="slate">{mode}</Badge>;
  }
}

function getConfirmationMethodBadge(method: string | undefined) {
  switch (method) {
    case 'webhook':
      return <Badge variant="success">Webhook</Badge>;
    case 'poll':
      return <Badge variant="warning">Poll</Badge>;
    case 'none':
      return <Badge variant="slate">None</Badge>;
    default:
      return <Badge variant="slate">{method || 'Unknown'}</Badge>;
  }
}

function getJobStatusBadge(status: string) {
  switch (status) {
    case 'succeeded':
      return <Badge variant="success">Succeeded</Badge>;
    case 'failed':
      return <Badge variant="danger">Failed</Badge>;
    case 'running':
      return <Badge variant="primary">Running</Badge>;
    case 'queued':
      return <Badge variant="warning">Queued</Badge>;
    default:
      return <Badge variant="slate">{status}</Badge>;
  }
}

function getJobTypeIcon(type: string) {
  switch (type) {
    case 'push':
      return <PlayIcon className="w-4 h-4 text-primary-500" />;
    case 'push_dry_run':
      return <PlayIcon className="w-4 h-4 text-warning-500" />;
    case 'feed_rebuild':
      return <ArrowPathIcon className="w-4 h-4 text-info-500" />;
    default:
      return <QueueListIcon className="w-4 h-4 text-slate-500" />;
  }
}

function formatDate(dateString: string | null): string {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleString();
}

function formatRelativeTime(dateString: string | null): string {
  if (!dateString) return '—';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

export default function DistPlatformDetailPage() {
  const { id } = useParams();
  const [jobsPage, setJobsPage] = useState(1);
  const [jobsStatusFilter, setJobsStatusFilter] = useState<string>('');

  // Fetch platform/distributor details
  const { data: platformResponse, isLoading, error, refetch } = useApi<DistributorDetailResponse>(
    `distribution/platforms/${id}`
  );

  // Extract distributor from response
  const platform = platformResponse?.distributor;

  // Build connector jobs URL with filters - use useMemo to ensure proper dependency tracking
  const jobsEndpoint = useMemo(() => {
    if (!id) return '';
    const params = new URLSearchParams();
    params.set('distributor_id', id);
    params.set('page', jobsPage.toString());
    params.set('pageSize', '10');
    if (jobsStatusFilter) {
      params.set('status', jobsStatusFilter);
    }
    return `admin/connector-jobs?${params.toString()}`;
  }, [id, jobsPage, jobsStatusFilter]);

  // Fetch connector jobs for this platform
  const { 
    data: jobsResponse, 
    isLoading: isLoadingJobs, 
    refetch: refetchJobs 
  } = useApi<ConnectorJobsListResponse>(
    jobsEndpoint,
    { immediate: !!id && !!jobsEndpoint }
  );

  const jobs = jobsResponse?.items || [];
  const jobsTotal = jobsResponse?.total || 0;
  const totalJobsPages = Math.ceil(jobsTotal / 10);

  // Count jobs by status
  const jobCounts = {
    queued: jobs.filter(j => j.connector_job_status === 'queued').length,
    running: jobs.filter(j => j.connector_job_status === 'running').length,
    succeeded: jobs.filter(j => j.connector_job_status === 'succeeded').length,
    failed: jobs.filter(j => j.connector_job_status === 'failed').length,
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !platform) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Link to="/distribution/platforms" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700">
          <ArrowLeftIcon className="w-4 h-4" />
          Back to Platforms
        </Link>
        <Card>
          <CardBody>
            <div className="text-center py-8">
              <ExclamationCircleIcon className="w-12 h-12 mx-auto text-danger-500 mb-4" />
              <p className="text-lg text-slate-600">
                {error || 'Platform not found'}
              </p>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Back link */}
      <Link to="/distribution/platforms" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700">
        <ArrowLeftIcon className="w-4 h-4" />
        Back to Platforms
      </Link>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <span className="text-3xl">{getPlatformIcon(platform.code)}</span>
            {getDistributorDisplayName(platform)}
          </h1>
          <div className="flex items-center gap-2 mt-2">
            {getStatusBadge(platform.active)}
            {getModeBadge(platform.mode)}
            {getConfirmationMethodBadge(platform.confirmation_method)}
          </div>
        </div>
        <Button onClick={() => refetch()}>
          <ArrowPathIcon className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Platform Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Basic Information */}
        <Card>
          <CardHeader title="Platform Information" />
          <CardBody className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-500">ID</label>
                <p className="text-sm font-mono text-slate-800 break-all">{platform.id}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-500">Code</label>
                <p className="text-sm font-mono text-slate-800">{platform.code}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-500">Connector Type</label>
                <p className="text-sm font-mono text-slate-800">{platform.connector_type}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-500">Mode</label>
                <p className="text-sm text-slate-800">{platform.mode}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-500">Confirmation Method</label>
                <p className="text-sm text-slate-800">{platform.confirmation_method || 'Not set'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-500">Active</label>
                <p className="text-sm text-slate-800">{platform.active ? 'Yes' : 'No'}</p>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* SLA & Timing */}
        <Card>
          <CardHeader title="SLA & Timing" />
          <CardBody className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-500">Pull SLA (Hours)</label>
                <p className="text-sm text-slate-800 flex items-center gap-1">
                  <ClockIcon className="w-4 h-4 text-slate-400" />
                  {platform.pull_sla_hours !== null && platform.pull_sla_hours !== undefined
                    ? `${platform.pull_sla_hours}h`
                    : 'Not set'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-500">Created At</label>
                <p className="text-sm text-slate-800">{formatDate(platform.created_at || null)}</p>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Configuration JSON */}
      <Card>
        <CardHeader title="Configuration (config_json)" />
        <CardBody>
          {platform.config_json && Object.keys(platform.config_json).length > 0 ? (
            <pre className="text-sm font-mono bg-slate-50 p-4 rounded-lg overflow-x-auto">
              {JSON.stringify(platform.config_json, null, 2)}
            </pre>
          ) : (
            <p className="text-sm text-slate-500 italic">No configuration data</p>
          )}
        </CardBody>
      </Card>

      {/* Connector Jobs Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-white">Connector Jobs</h3>
              <span className="text-sm font-normal text-slate-500">
                ({jobsTotal} total)
              </span>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={jobsStatusFilter}
                onChange={(e) => {
                  setJobsStatusFilter(e.target.value);
                  setJobsPage(1);
                }}
                className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">All Statuses</option>
                <option value="queued">Queued</option>
                <option value="running">Running</option>
                <option value="succeeded">Succeeded</option>
                <option value="failed">Failed</option>
              </select>
              <Button variant="ghost" size="sm" onClick={() => refetchJobs()}>
                <ArrowPathIcon className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {/* Job Status Summary */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-warning-50 rounded-lg p-3 text-center">
              <div className="flex items-center justify-center gap-1 text-warning-700">
                <QueueListIcon className="w-4 h-4" />
                <span className="text-lg font-semibold">{jobCounts.queued}</span>
              </div>
              <span className="text-xs text-warning-600">Queued</span>
            </div>
            <div className="bg-primary-50 rounded-lg p-3 text-center">
              <div className="flex items-center justify-center gap-1 text-primary-700">
                <PlayIcon className="w-4 h-4" />
                <span className="text-lg font-semibold">{jobCounts.running}</span>
              </div>
              <span className="text-xs text-primary-600">Running</span>
            </div>
            <div className="bg-success-50 rounded-lg p-3 text-center">
              <div className="flex items-center justify-center gap-1 text-success-700">
                <CheckCircleIcon className="w-4 h-4" />
                <span className="text-lg font-semibold">{jobCounts.succeeded}</span>
              </div>
              <span className="text-xs text-success-600">Succeeded</span>
            </div>
            <div className="bg-danger-50 rounded-lg p-3 text-center">
              <div className="flex items-center justify-center gap-1 text-danger-700">
                <ExclamationCircleIcon className="w-4 h-4" />
                <span className="text-lg font-semibold">{jobCounts.failed}</span>
              </div>
              <span className="text-xs text-danger-600">Failed</span>
            </div>
          </div>

          {/* Jobs Table */}
          {isLoadingJobs ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <QueueListIcon className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p>No connector jobs found for this platform</p>
              {jobsStatusFilter && (
                <p className="text-sm mt-1">
                  Try changing the status filter or check if jobs exist
                </p>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Type</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Delivery ID</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Attempts</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Started</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Completed</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map((job) => (
                      <tr key={job.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            {getJobTypeIcon(job.connector_job_type)}
                            <span className="text-sm font-medium text-slate-700">
                              {job.connector_job_type.replace(/_/g, ' ')}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {getJobStatusBadge(job.connector_job_status)}
                        </td>
                        <td className="py-3 px-4">
                          <Link 
                            to={`/distribution/books/${job.delivery_id}`}
                            className="text-sm font-mono text-primary-600 hover:text-primary-700 hover:underline"
                          >
                            {job.delivery_id.slice(0, 8)}...
                          </Link>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm text-slate-600">{job.attempt_count}</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm text-slate-600" title={formatDate(job.started_at)}>
                            {formatRelativeTime(job.started_at)}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm text-slate-600" title={formatDate(job.completed_at)}>
                            {formatRelativeTime(job.completed_at)}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          {job.last_error ? (
                            <span 
                              className="text-sm text-danger-600 truncate block max-w-[200px]" 
                              title={job.last_error}
                            >
                              {job.last_error}
                            </span>
                          ) : (
                            <span className="text-sm text-slate-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalJobsPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
                  <div className="text-sm text-slate-500">
                    Page {jobsPage} of {totalJobsPages}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setJobsPage(p => Math.max(1, p - 1))}
                      disabled={jobsPage === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setJobsPage(p => Math.min(totalJobsPages, p + 1))}
                      disabled={jobsPage === totalJobsPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>

      {/* Raw Data (for debugging) */}
      <Card>
        <CardHeader title="Raw Platform Data (Debug)" />
        <CardBody>
          <pre className="text-xs font-mono bg-slate-50 p-4 rounded-lg overflow-x-auto max-h-[300px]">
            {JSON.stringify(platform, null, 2)}
          </pre>
        </CardBody>
      </Card>
    </div>
  );
}
